import React, {
  useState, useRef, useContext, useEffect, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  MenuList,
  Typography,
  Popper,
  Paper,
  ClickAwayListener,
  Button,
} from '@material-ui/core';
import * as Icons from '@material-ui/icons';
import Flag from 'react-world-flags';
import { getCookie, setCookie } from '../../utils/cookies'; // adjust the path
import { useStyles, CustomMenuItem } from './styles';
import {
  COUNTRY_ID,
  COUNTRY_MY,
  COUNTRY_VN,
} from '../../localization/features/constants';
import { AuthContext } from '../../contexts';

const LANGUAGES = {
  en: { label: 'English', isoCode: 'US', country_code: COUNTRY_MY },
  'id-ID': { label: 'Indonesia', isoCode: 'ID', country_code: COUNTRY_ID },
  'vi-VN': { label: 'Vietnam', isoCode: 'VN', country_code: COUNTRY_VN },
};

const COOKIE_NAME = 'preferred_language';

const LangSelector = () => {
  const authContext = useContext(AuthContext);
  const classes = useStyles();
  const { i18n } = useTranslation();
  const { country } = authContext.state;
  const anchorRef = useRef(null);

  const getLangByCountry = (countryCode) => {
    if (countryCode === COUNTRY_ID) return 'id-ID';
    if (countryCode === COUNTRY_VN) return 'vi-VN';
    return 'en';
  };

  const shouldShowLang = (code) => {
    if (country.country_code === COUNTRY_ID) {
      return code === 'en' || code === 'id-ID';
    }
    if (country.country_code === COUNTRY_VN) {
      return code === 'en' || code === 'vi-VN';
    }
    if (country.country_code === COUNTRY_MY) {
      return code === 'en';
    }

    return true;
  };

  const getInitialLang = () => {
    const cookieLang = getCookie(COOKIE_NAME);
    if (cookieLang && LANGUAGES[cookieLang]) {
      return cookieLang;
    }
    if (Object.keys(LANGUAGES).includes(i18n.language)) {
      return i18n.language;
    }
    return getLangByCountry(country.country_code);
  };

  const [currentLang, setCurrentLang] = useState(getInitialLang());
  const [open, setOpen] = useState(false);

  // Sync language on mount and when country changes
  useEffect(() => {
    const langByCountry = getLangByCountry(country.country_code);
    const cookieLang = getCookie(COOKIE_NAME);

    const newLang = cookieLang && LANGUAGES[cookieLang]
      ? cookieLang
      : langByCountry;

    if (newLang !== i18n.language) {
      i18n.changeLanguage(newLang);
    }

    setCurrentLang(newLang);
    setCookie(COOKIE_NAME, newLang, 365);
  }, [country]);

  const handleToggle = () => setOpen((prev) => !prev);

  const handleClose = (event) => {
    if (anchorRef.current?.contains(event.target)) return;
    setOpen(false);
  };

  const handleChange = (langCode) => {
    i18n.changeLanguage(langCode);
    setCurrentLang(langCode);
    setCookie(COOKIE_NAME, langCode, { expires: 365 });
    setOpen(false);
  };

  const languageOptions = useMemo(
    () => Object.entries(LANGUAGES).filter(([code]) => shouldShowLang(code)),
    [country],
  );

  return (
    <>
      <Button
        ref={anchorRef}
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        className={classes.flagOnly}
      >
        <Flag code={LANGUAGES[currentLang]?.isoCode} className={classes.flagIcon} />
        <Icons.ArrowDropDown className={classes.arrow} />
      </Button>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        className={classes.popper}
        disablePortal
      >
        <Paper>
          <ClickAwayListener onClickAway={handleClose}>
            <MenuList id="language-menu" disablePadding>
              {languageOptions.map(([code, { label, isoCode }]) => (
                <CustomMenuItem
                  key={code}
                  selected={currentLang === code}
                  onClick={() => handleChange(code)}
                >
                  <Flag code={isoCode} className={classes.flagIcon} />
                  <Typography>{label}</Typography>
                </CustomMenuItem>
              ))}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </>
  );
};

export default LangSelector;
